import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * Login is optional. Every visitor receives an anonymous `anon_id` cookie
 * from the backend on first call to /api/auth/me, so `user` is always
 * populated after auth resolution. This gate only blocks the password-flow
 * redirects and shows the brief loading state.
 *
 * Failure mode — when auth resolution times out or the backend is
 * unreachable, render a friendly escape hatch so the visitor is never
 * trapped on "Verifying handshake...".
 */
const SET_PW_PATH = "/set-password";
const UNLOCK_PATH = "/unlock";
const ONBOARD_PATH = "/onboarding";

function isAnonymous(user) {
  return !!user && user.user_id?.startsWith?.("anon_");
}

export default function ProtectedRoute({ children, allowPasswordFlow = false }) {
  const { user, loading, authError, resetHandshake, refresh } = useAuth();
  const location = useLocation();
  const [showStuckEscape, setShowStuckEscape] = useState(false);

  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setShowStuckEscape(true), 4000);
    return () => clearTimeout(t);
  }, [loading]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#09090b] text-zinc-100 px-6"
        data-testid="auth-loading"
      >
        <div className="flex flex-col items-center gap-4 max-w-sm text-center">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.35em] text-emerald-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Verifying handshake…
          </div>
          {showStuckEscape && (
            <div
              className="mt-4 border border-amber-500/40 bg-amber-500/5 p-4 space-y-3"
              data-testid="auth-stuck-escape"
            >
              <p className="text-[11px] text-zinc-300 leading-relaxed">
                The handshake is taking longer than expected. The Sentinel
                may have lost its tether to your session.
              </p>
              <div className="flex gap-2 justify-center">
                <button
                  type="button"
                  onClick={() => refresh()}
                  data-testid="auth-retry-btn"
                  className="px-3 py-2 text-[10px] uppercase tracking-[0.3em] border border-zinc-700 text-zinc-300 hover:border-emerald-500 hover:text-emerald-400 transition-colors"
                >
                  Retry
                </button>
                <button
                  type="button"
                  onClick={resetHandshake}
                  data-testid="auth-reset-btn"
                  className="px-3 py-2 text-[10px] uppercase tracking-[0.3em] border border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-zinc-950 transition-colors"
                >
                  Reset Handshake
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    if (authError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-[#09090b] text-zinc-100 px-6"
          data-testid="auth-error"
        >
          <div className="max-w-md w-full border border-rose-500/30 bg-[#0b0b0d] p-8 space-y-4">
            <div className="text-[10px] uppercase tracking-[0.35em] text-rose-400">
              Sentinel · Handshake unreachable
            </div>
            <h1 className="font-heading text-2xl">
              {authError === "timeout"
                ? "The handshake timed out."
                : "Couldn't reach the Sentinel."}
            </h1>
            <p className="text-xs text-zinc-400 leading-relaxed">
              This usually means the Sentinel is catching its breath, or your
              cached session is out of sync. Reset the handshake and try again.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => refresh()}
                data-testid="auth-retry-btn"
                className="px-5 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 text-xs uppercase tracking-[0.3em]"
              >
                Retry
              </button>
              <button
                onClick={resetHandshake}
                data-testid="auth-reset-btn"
                className="px-5 py-3 border border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-zinc-950 text-xs uppercase tracking-[0.3em]"
              >
                Reset Handshake
              </button>
            </div>
          </div>
        </div>
      );
    }
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (!isAnonymous(user)) {
    if (!user.password_set && location.pathname !== SET_PW_PATH) {
      return <Navigate to={SET_PW_PATH} replace />;
    }
    if (user.password_set && !user.password_unlocked && location.pathname !== UNLOCK_PATH) {
      return <Navigate to={UNLOCK_PATH} replace />;
    }
    // Mandatory wallet-handle registration. Once the user has finished
    // password setup + unlock, force them through /onboarding to claim a
    // public @username before they can use the rest of the app.
    if (
      user.password_set
      && user.password_unlocked
      && !user.username_set
      && location.pathname !== ONBOARD_PATH
      && location.pathname !== SET_PW_PATH
      && location.pathname !== UNLOCK_PATH
    ) {
      return <Navigate to={ONBOARD_PATH} replace />;
    }
    if (allowPasswordFlow) {
      if (location.pathname === SET_PW_PATH && user.password_set) {
        return <Navigate to={user.username_set ? "/welcome" : ONBOARD_PATH} replace />;
      }
      if (location.pathname === UNLOCK_PATH && user.password_unlocked) {
        return <Navigate to={user.username_set ? "/welcome" : ONBOARD_PATH} replace />;
      }
    }
  } else if (allowPasswordFlow) {
    return <Navigate to="/welcome" replace />;
  }

  return children;
}
